import { useState, useCallback, useMemo, useEffect } from "react";
import { Task, TaskStartDatePeriod } from "Models/task";
import { TimeSlotTask } from "Models/time.slot.task";
import TaskDateUtils from "Util/dateUtils";
import { useAppState } from "Context/StateContext";

export interface UseTaskDateManagementProps {
  initialTask: Task;
  onTaskUpdate: (updatedTask: Task) => void;
}

export interface UseTaskDateManagementReturn {
  // Date values for inputs
  startDateInput: string;
  endDateInput: string;
  startTimeInput: string;
  endTimeInput: string;

  // Handlers
  handleStartDateChange: (dateInput: string) => void;
  handleEndDateChange: (dateInput: string) => void;
  handleStartTimeChange: (timeInput: string) => void;
  handleEndTimeChange: (timeInput: string) => void;
  handlePeriodChange: (props: {
    period: TaskStartDatePeriod;
    currentDate?: Date;
    timezone?: string;
  }) => void;
  handleTimeSlotChange: (timeSlots: TimeSlotTask[]) => void;

  // Validation
  isEndDateValid: boolean;
  isStartTimeValid: boolean;
  isEndTimeValid: boolean;

  // Computed values
  isMultiDayTask: boolean;
  minimumEndDate: string;

  // Utilities
  createTaskDates: (dateInput: string, timeInput?: string) => string;
  validateDateRange: () => boolean;
}

export const useTaskDateManagement = ({
  initialTask,
  onTaskUpdate,
}: UseTaskDateManagementProps): UseTaskDateManagementReturn => {
  const { formatWithUserTimezone, userTimezone } = useAppState();
  const [currentTask, setCurrentTask] = useState<Task>(initialTask);

  const timezone = userTimezone?.name || Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    if (
      (initialTask && !currentTask) ||
      (initialTask && currentTask && JSON.stringify(initialTask) !== JSON.stringify(currentTask))
    ) {
      setCurrentTask((prev) => ({ ...(prev || {}), ...(initialTask || {}) }));
    }
  }, [initialTask]);

  // Extract current values for inputs
  const startDateInput = useMemo(() => {
    const startDate = currentTask?.startDate
      ? TaskDateUtils.extractDateForInput(currentTask.startDate, {
          formatFunction: formatWithUserTimezone,
        })
      : "";

    return startDate;
  }, [currentTask?.startDate]);

  const endDateInput = useMemo(
    () =>
      currentTask?.endDate
        ? TaskDateUtils.extractDateForInput(currentTask.endDate, {
            formatFunction: formatWithUserTimezone,
          })
        : "",
    [currentTask?.endDate]
  );

  const startTimeInput = useMemo(() => {
    const startTime = currentTask?.startDate
      ? TaskDateUtils.extractTimeForInput(currentTask.startDate, {
          formatFunction: formatWithUserTimezone,
        })
      : "";

    return startTime;
  }, [currentTask?.startDate]);

  const endTimeInput = useMemo(
    () =>
      currentTask?.endDate
        ? TaskDateUtils.extractTimeForInput(currentTask.endDate, {
            formatFunction: formatWithUserTimezone,
          })
        : "",
    [currentTask?.endDate]
  );

  // Computed properties
  const isMultiDayTask = useMemo(
    () =>
      [
        TaskStartDatePeriod.MULTI_DAY,
        TaskStartDatePeriod.VARIABLE_TIME,
        TaskStartDatePeriod.FIXED_TIME,
      ].includes(currentTask?.period!),
    [currentTask?.period]
  );

  const minimumEndDate = useMemo(
    () =>
      startDateInput ||
      TaskDateUtils.getTodayForInput(undefined, { formatFunction: formatWithUserTimezone }),
    [startDateInput]
  );

  // Validation
  const isEndDateValid = useMemo(
    () => TaskDateUtils.isEndDateValid(currentTask?.startDate || "", currentTask?.endDate || ""),
    [currentTask?.startDate, currentTask?.endDate]
  );

  const isStartTimeValid = useMemo(
    () => (currentTask?.startDate ? TaskDateUtils.isValidDate(currentTask.startDate) : true),
    [currentTask?.startDate]
  );

  const isEndTimeValid = useMemo(() => {
    if (!currentTask?.endDate || !currentTask?.startDate) return true;

    // For same-day tasks, ensure end time is after start time
    if (startDateInput === endDateInput) {
      const startDateTime = new Date(`${startDateInput}T${startTimeInput}`);
      const endDateTime = new Date(`${endDateInput}T${endTimeInput}`);
      return endDateTime > startDateTime;
    }

    return true;
  }, [
    currentTask?.startDate,
    currentTask?.endDate,
    startDateInput,
    endDateInput,
    startTimeInput,
    endTimeInput,
  ]);

  // Update task and notify parent
  const updateTask = useCallback(
    (updates: Partial<Task>) => {
      const updatedTask = {
        ...currentTask,
        ...updates,
        whatHasChanged: [...(currentTask?.whatHasChanged || []), ...Object.keys(updates)],
      };

      setCurrentTask(updatedTask);
      onTaskUpdate(updatedTask);
    },
    [currentTask, onTaskUpdate]
  );

  // Handlers
  const handleStartDateChange = useCallback(
    (dateInput: string) => {
      if (!dateInput) return;

      const existingTime = currentTask?.startDate
        ? TaskDateUtils.extractTimeForInput(currentTask.startDate, {
            formatFunction: formatWithUserTimezone,
          })
        : undefined;

      const startDate = TaskDateUtils.createTaskDateTime(
        {
          dateInput,
          timeInput: existingTime,
          preserveExistingTime: true,
          existingDateString: currentTask?.startDate,
        },
        timezone
      );

      let updates: Partial<Task> = { startDate };

      // Auto-adjust end date if it becomes invalid
      if (currentTask?.endDate) {
        const currentEndDateInput = TaskDateUtils.extractDateForInput(currentTask.endDate, {
          formatFunction: formatWithUserTimezone,
        });

        if (
          dateInput > currentEndDateInput ||
          (dateInput === currentEndDateInput && isMultiDayTask)
        ) {
          // Move end date forward
          const newEndDate = new Date(dateInput);
          newEndDate.setDate(newEndDate.getDate() + (isMultiDayTask ? 1 : 0));

          const existingEndTime = TaskDateUtils.extractTimeForInput(currentTask.endDate, {
            formatFunction: formatWithUserTimezone,
          });
          const endDate = TaskDateUtils.createTaskDateTime(
            {
              dateInput: TaskDateUtils.extractDateForInput(newEndDate.toISOString()),
              timeInput: existingEndTime,
              preserveExistingTime: true,
              existingDateString: currentTask.endDate,
            },
            timezone
          );

          updates.endDate = endDate;
        }
      }

      updateTask(updates);
    },
    [currentTask, isMultiDayTask, updateTask]
  );

  const handleEndDateChange = useCallback(
    (dateInput: string) => {
      if (!dateInput) return;

      const existingTime = currentTask?.endDate
        ? TaskDateUtils.extractTimeForInput(currentTask.endDate, {
            formatFunction: formatWithUserTimezone,
          })
        : undefined;

      const endDate = TaskDateUtils.createTaskDateTime(
        {
          dateInput,
          timeInput: existingTime,
          preserveExistingTime: true,
          existingDateString: currentTask?.endDate,
        },
        timezone
      );

      updateTask({ endDate });
    },
    [currentTask, updateTask]
  );

  const handleStartTimeChange = useCallback(
    (timeInput: string) => {
      if (!timeInput) return;

      const existingDate = currentTask?.startDate
        ? TaskDateUtils.extractDateForInput(currentTask.startDate)
        : TaskDateUtils.getTodayForInput();

      const startDate = TaskDateUtils.createTaskDateTime(
        { dateInput: existingDate, timeInput },
        timezone
      );

      let updates: Partial<Task> = { startDate };

      // Auto-adjust end time for same-day tasks if end time becomes invalid
      if (currentTask?.endDate && startDateInput === endDateInput) {
        const currentEndTime = TaskDateUtils.extractTimeForInput(currentTask.endDate, {
          formatFunction: formatWithUserTimezone,
        });
        const startDateTime = new Date(`${existingDate}T${timeInput}`);
        const endDateTime = new Date(`${endDateInput}T${currentEndTime}`);

        if (endDateTime <= startDateTime) {
          // Add 1 hour to start time for end time
          const newEndDateTime = new Date(startDateTime.getTime() + 60 * 60 * 1000);
          const newEndTime = TaskDateUtils.extractTimeForInput(newEndDateTime.toISOString(), {
            formatFunction: formatWithUserTimezone,
          });
          const endDate = TaskDateUtils.createTaskDateTime(
            { dateInput: endDateInput, timeInput: newEndTime },
            timezone
          );
          updates.endDate = endDate;
        }
      }

      updateTask(updates);
    },
    [currentTask, startDateInput, endDateInput, updateTask]
  );

  const handleEndTimeChange = useCallback(
    (timeInput: string) => {
      if (!timeInput) return;

      const existingDate = currentTask?.endDate
        ? TaskDateUtils.extractDateForInput(currentTask.endDate, {
            formatFunction: formatWithUserTimezone,
          })
        : TaskDateUtils.getTodayForInput(undefined, { formatFunction: formatWithUserTimezone });

      const endDate = TaskDateUtils.createTaskDateTime(
        { dateInput: existingDate, timeInput },
        timezone
      );

      updateTask({ endDate });
    },
    [currentTask, updateTask]
  );

  const handlePeriodChange = useCallback(
    ({
      period,
      currentDate: currentInputDate,
      timezone: currentTimezone,
    }: {
      period: TaskStartDatePeriod;
      currentDate?: Date;
      timezone?: string;
    }) => {
      const currentStartDate = currentInputDate?.toISOString() ?? currentTask?.startDate;
      let props: any = { formatFunction: formatWithUserTimezone };

      if (currentTimezone) {
        props = { timezone: currentTimezone };
      }

      const currentDate = currentStartDate
        ? TaskDateUtils.extractDateForInput(currentStartDate, props)
        : TaskDateUtils.getTodayForInput(undefined, props);

      let updates: Partial<Task> = { period };

      if (
        [
          TaskStartDatePeriod.MORNING,
          TaskStartDatePeriod.AFTERNOON,
          TaskStartDatePeriod.EVENING,
          TaskStartDatePeriod.FULL_DAY,
        ].includes(period)
      ) {
        // Use predefined periods
        const dates = TaskDateUtils.createPeriodDates(
          currentDate,
          period,
          currentStartDate,
          currentTimezone || timezone
        );
        updates.startDate = dates.startDate;
        updates.endDate = dates.endDate;
      } else if (period === TaskStartDatePeriod.MULTI_DAY) {
        // Multi-day: start today, end tomorrow
        const tomorrow = new Date(
          TaskDateUtils.getTodayForInput(undefined, { formatFunction: formatWithUserTimezone })
        );
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowInput = TaskDateUtils.extractDateForInput(tomorrow.toISOString(), props);

        const dates = TaskDateUtils.createMultiDayDates(
          currentDate,
          tomorrowInput,
          undefined,
          undefined,
          true,
          currentStartDate,
          currentTask?.endDate,
          currentTimezone || timezone
        );
        updates.startDate = dates.startDate;
        updates.endDate = dates.endDate;
      } else if (period === TaskStartDatePeriod.VARIABLE_TIME) {
        // Initialize with one time slot
        updates.timeSlots = [
          {
            date: TaskDateUtils.createTaskDateTime(
              { dateInput: currentDate, timeInput: "09:00" },
              currentTimezone || timezone
            ),
            from: "09:00",
            to: "10:00",
          } as TimeSlotTask,
        ];
      } else {
        // Custom or other periods - preserve existing times or add buffer
        const startDate =
          currentStartDate ||
          TaskDateUtils.createTaskDateTime(
            { dateInput: currentDate, timeInput: "09:00" },
            currentTimezone || timezone
          );
        const endDate = TaskDateUtils.addBufferTime(startDate, period);
        updates.startDate = startDate;
        updates.endDate = endDate;
      }

      updateTask({ ...(currentTask || {}), ...updates });
    },
    [currentTask, updateTask]
  );

  const handleTimeSlotChange = useCallback(
    (timeSlots: TimeSlotTask[]) => {
      updateTask({ timeSlots });
    },
    [updateTask]
  );

  // Utility functions
  const createTaskDates = useCallback(
    (dateInput: string, timeInput?: string) => {
      return TaskDateUtils.createTaskDateTime(
        {
          dateInput,
          timeInput,
          preserveExistingTime: true,
          existingDateString: currentTask?.startDate,
        },
        timezone
      );
    },
    [currentTask?.startDate]
  );

  const validateDateRange = useCallback(() => {
    if (!currentTask?.startDate || !currentTask?.endDate) return true;

    return TaskDateUtils.isEndDateValid(currentTask.startDate, currentTask.endDate);
  }, [currentTask?.startDate, currentTask?.endDate]);

  return {
    // Date values for inputs
    startDateInput,
    endDateInput,
    startTimeInput,
    endTimeInput,

    // Handlers
    handleStartDateChange,
    handleEndDateChange,
    handleStartTimeChange,
    handleEndTimeChange,
    handlePeriodChange,
    handleTimeSlotChange,

    // Validation
    isEndDateValid,
    isStartTimeValid,
    isEndTimeValid,

    // Computed values
    isMultiDayTask,
    minimumEndDate,

    // Utilities
    createTaskDates,
    validateDateRange,
  };
};

export default useTaskDateManagement;
